import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Gift,
  Star,
  Crown,
  Trophy,
  Zap,
  Target,
  Clock,
  ShoppingBag,
  Coffee,
  Car,
  Plane,
  Smartphone,
  Gamepad2,
  Heart,
  Sparkles,
  Award,
  Coins,
  CreditCard,
  TrendingUp,
  Calendar,
  Users,
  ExternalLink,
  Check,
  Loader2,
  AlertCircle
} from 'lucide-react'
import { RewardsAPI, extractArray } from '../services/api'

const categories = [
  { id: 'dining', name: 'Dining', icon: Coffee, multiplier: 3, color: 'from-orange-500 to-red-500' },
  { id: 'gas', name: 'Gas Stations', icon: Car, multiplier: 2, color: 'from-blue-500 to-cyan-500' },
  { id: 'groceries', name: 'Groceries', icon: ShoppingBag, multiplier: 2, color: 'from-green-500 to-emerald-500' },
  { id: 'travel', name: 'Travel', icon: Plane, multiplier: 5, color: 'from-purple-500 to-pink-500' },
  { id: 'streaming', name: 'Streaming', icon: Smartphone, multiplier: 4, color: 'from-indigo-500 to-purple-500' },
  { id: 'gaming', name: 'Gaming', icon: Gamepad2, multiplier: 3, color: 'from-violet-500 to-indigo-500' }
]

export default function RewardsCenter() {
  const [activeTab, setActiveTab] = useState('overview')
  const [selectedCategory, setSelectedCategory] = useState('all')

  // Data states
  const [userStats, setUserStats] = useState({
    totalPoints: 0,
    tier: 'basic',
    nextTier: 'silver',
    pointsToNextTier: 5000,
    cashbackEarned: 0,
    streakDays: 0
  })
  const [tiers, setTiers] = useState({})
  const [availableRewards, setAvailableRewards] = useState([])
  const [recentActivity, setRecentActivity] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [redeeming, setRedeeming] = useState(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    setError(null)

    try {
      console.log('Fetching rewards data...')

      const [summaryRes, catalogRes, historyRes, tiersRes] = await Promise.all([
        RewardsAPI.getSummary(),
        RewardsAPI.getCatalog(),
        RewardsAPI.getHistory(),
        RewardsAPI.getTiers()
      ])

      console.log('Summary response:', summaryRes)
      console.log('Catalog response:', catalogRes)
      console.log('History response:', historyRes)
      console.log('Tiers response:', tiersRes)

      if (summaryRes.success) {
        const data = summaryRes.data
        setUserStats({
          totalPoints: parseInt(data.current_points) || parseInt(data.currentPoints) || 0,
          tier: data.tier || 'basic',
          nextTier: getNextTier(data.tier || 'basic'),
          pointsToNextTier: getPointsToNextTier(data.tier || 'basic', parseInt(data.current_points) || 0),
          cashbackEarned: parseFloat(data.lifetime_cashback) || parseFloat(data.lifetimeCashback) || 0,
          streakDays: parseInt(data.current_streak_days) || parseInt(data.streakDays) || 0
        })
      }

      if (catalogRes.success) {
        const rewards = extractArray(catalogRes, 'rewards')
        setAvailableRewards(rewards.map(r => ({
          id: r.id,
          name: r.name,
          points: parseInt(r.points_required) || 0,
          value: parseFloat(r.cash_value) || 0,
          category: r.category || 'gift-cards',
          image: getCategoryEmoji(r.category),
          popular: r.is_featured,
          description: r.description
        })))
      }

      if (historyRes.success) {
        const history = extractArray(historyRes)
        setRecentActivity(history.slice(0, 10).map(h => ({
          id: h.id,
          type: h.type,
          description: h.description || h.reward_name || 'Transaction',
          points: parseInt(h.points) || 0,
          time: formatTimeAgo(h.created_at)
        })))
      }

      if (tiersRes.success && tiersRes.data.tiers) {
        setTiers(tiersRes.data.tiers)
      }

    } catch (err) {
      console.error('Rewards fetch error:', err)
      setError(`Failed to load rewards data: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleRedeem = async (rewardId) => {
    setRedeeming(rewardId)
    try {
      const res = await RewardsAPI.redeem(rewardId)
      if (res.success) {
        // Refresh data after redemption
        fetchData()
      } else {
        setError(res.message || 'Failed to redeem reward')
      }
    } catch (err) {
      setError(`Failed to redeem: ${err.message}`)
    } finally {
      setRedeeming(null)
    }
  }

  const getNextTier = (currentTier) => {
    const tierOrder = ['basic', 'silver', 'gold', 'platinum', 'diamond']
    const index = tierOrder.indexOf(currentTier)
    return index < tierOrder.length - 1 ? tierOrder[index + 1] : tierOrder[tierOrder.length - 1]
  }

  const getPointsToNextTier = (currentTier, currentPoints) => {
    const tierPoints = { basic: 0, silver: 5000, gold: 15000, platinum: 50000, diamond: 100000 }
    const nextTier = getNextTier(currentTier)
    return Math.max(0, tierPoints[nextTier] - currentPoints)
  }

  const getCategoryEmoji = (category) => {
    const emojis = {
      'gift-cards': '🎁',
      'cash': '💵',
      'travel': '✈️',
      'subscriptions': '📺',
      'electronics': '🎧',
      'merchandise': '🛍️'
    }
    return emojis[category] || '🎁'
  }

  const formatTimeAgo = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const now = new Date()
    const diff = now - date
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const days = Math.floor(hours / 24)

    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`
    return 'Just now'
  }

  // Tier configuration
  const tierConfig = [
    { name: 'Silver', minPoints: 0, color: 'from-gray-400 to-gray-500', benefits: ['1x points', 'Basic support'] },
    { name: 'Gold', minPoints: 5000, color: 'from-yellow-400 to-orange-500', benefits: ['1.5x points', 'Priority support', '1% bonus cashback'] },
    { name: 'Platinum', minPoints: 15000, color: 'from-purple-400 to-indigo-500', benefits: ['2x points', 'Premium support', '2% bonus cashback', 'Airport lounge access'] },
    { name: 'Diamond', minPoints: 50000, color: 'from-cyan-400 to-blue-500', benefits: ['3x points', 'Concierge service', '3% bonus cashback', 'Exclusive events'] }
  ]

  const currentTier = tierConfig.find(t => t.name.toLowerCase() === userStats.tier.toLowerCase()) || tierConfig[0]
  const nextTierObj = tierConfig.find(t => t.name.toLowerCase() === userStats.nextTier.toLowerCase()) || tierConfig[1]
  const tierProgress = nextTierObj.minPoints > currentTier.minPoints
    ? ((userStats.totalPoints - currentTier.minPoints) / (nextTierObj.minPoints - currentTier.minPoints)) * 100
    : 100

  const filteredRewards = selectedCategory === 'all'
    ? availableRewards
    : availableRewards.filter(reward => reward.category === selectedCategory)

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Top Bar */}
      <div className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Rewards Center</h1>
              <p className="text-sm text-slate-500 mt-0.5">Earn points and unlock rewards</p>
            </div>

            <div className="flex items-center gap-3">
              <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-2">
                <div className="flex items-center gap-2">
                  <Coins className="w-4 h-4 text-slate-600" />
                  <span className="text-lg font-semibold text-slate-900">{userStats.totalPoints.toLocaleString()}</span>
                </div>
                <p className="text-xs text-slate-500 text-center">Points</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-8 max-w-[1600px] mx-auto">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <p className="text-red-700">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">✕</button>
          </div>
        )}

        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          {/* Tier Progress */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-6 bg-white border border-slate-200 rounded-xl mb-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-amber-50 flex items-center justify-center">
                  <Crown className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-slate-900 font-semibold text-sm capitalize">{userStats.tier} Member</h3>
                  <p className="text-slate-500 text-xs">{userStats.pointsToNextTier.toLocaleString()} points to {userStats.nextTier}</p>
                </div>
              </div>

              <div className="text-right">
                <p className="text-slate-900 font-semibold text-sm">Level Progress</p>
                <p className="text-slate-500 text-xs">{tierProgress.toFixed(1)}%</p>
              </div>
            </div>

            <div className="w-full bg-slate-100 rounded-full h-2 mb-4">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(tierProgress, 100)}%` }}
                transition={{ duration: 1, delay: 0.5 }}
                className="h-2 rounded-full bg-amber-600"
              />
            </div>

            <div className="flex justify-between text-xs text-slate-500 capitalize">
              <span>{currentTier.name}</span>
              <span>{nextTierObj.name}</span>
            </div>
          </motion.div>

          {/* Tabs */}
          <div className="flex gap-2 bg-slate-100 rounded-lg p-1">
            {['overview', 'rewards', 'activity'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-2.5 rounded-md transition-colors capitalize text-sm font-medium ${
                  activeTab === tab
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid md:grid-cols-4 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="p-5 bg-white border border-slate-200 rounded-xl"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-medium text-slate-500">Cashback Earned</h3>
                  <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 text-emerald-600" />
                  </div>
                </div>
                <p className="text-xl font-bold text-slate-900">${userStats.cashbackEarned.toFixed(2)}</p>
                <p className="text-xs text-slate-500 mt-1">Lifetime</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="p-5 bg-white border border-slate-200 rounded-xl"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-medium text-slate-500">Total Points</h3>
                  <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                    <Coins className="w-4 h-4 text-blue-600" />
                  </div>
                </div>
                <p className="text-xl font-bold text-slate-900">{userStats.totalPoints.toLocaleString()}</p>
                <p className="text-xs text-slate-500 mt-1">Available</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="p-5 bg-white border border-slate-200 rounded-xl"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-medium text-slate-500">Streak Days</h3>
                  <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center">
                    <Zap className="w-4 h-4 text-orange-600" />
                  </div>
                </div>
                <p className="text-xl font-bold text-slate-900">{userStats.streakDays}</p>
                <p className="text-xs text-slate-500 mt-1">Days active</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="p-5 bg-white border border-slate-200 rounded-xl"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-medium text-slate-500">Tier Benefits</h3>
                  <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center">
                    <Star className="w-4 h-4 text-purple-600" />
                  </div>
                </div>
                <p className="text-xl font-bold text-slate-900">{currentTier.benefits.length}</p>
                <p className="text-xs text-slate-500 mt-1">Active perks</p>
              </motion.div>
            </div>

            {/* Earning Categories */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="p-6 bg-white border border-slate-200 rounded-xl"
            >
              <h3 className="text-sm font-semibold text-slate-900 mb-6">Earning Categories</h3>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map((category, index) => {
                  const IconComponent = category.icon
                  const colorMap = {
                    'from-orange-500 to-red-500': 'bg-orange-50 text-orange-600',
                    'from-blue-500 to-cyan-500': 'bg-blue-50 text-blue-600',
                    'from-green-500 to-emerald-500': 'bg-emerald-50 text-emerald-600',
                    'from-purple-500 to-pink-500': 'bg-purple-50 text-purple-600',
                    'from-indigo-500 to-purple-500': 'bg-indigo-50 text-indigo-600',
                    'from-violet-500 to-indigo-500': 'bg-violet-50 text-violet-600'
                  }
                  const pastelColor = colorMap[category.color] || 'bg-slate-50 text-slate-600'

                  return (
                    <motion.div
                      key={category.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`p-2 rounded-lg ${pastelColor.split(' ')[0]}`}>
                          <IconComponent className={`w-5 h-5 ${pastelColor.split(' ')[1]}`} />
                        </div>
                        <h4 className="text-slate-900 font-medium text-sm">{category.name}</h4>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-slate-500 text-xs">Earning Rate</span>
                        <span className="text-slate-900 font-semibold text-xs">{category.multiplier}x points</span>
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            </motion.div>
          </div>
        )}

        {/* Rewards Tab */}
        {activeTab === 'rewards' && (
          <div className="space-y-6">
            {/* Category Filter */}
            <div className="flex gap-2 overflow-x-auto">
              {['all', 'gift-cards', 'cash', 'subscriptions', 'electronics'].map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg whitespace-nowrap transition-colors capitalize text-sm font-medium ${
                    selectedCategory === category
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {category.replace('-', ' ')}
                </button>
              ))}
            </div>

            {/* Rewards Grid */}
            {filteredRewards.length === 0 ? (
              <div className="text-center py-12 bg-slate-50 rounded-xl">
                <Gift className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No rewards available in this category</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredRewards.map((reward, index) => (
                  <motion.div
                    key={reward.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="group relative p-6 bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition-all"
                  >
                    {/* Badges */}
                    <div className="absolute top-4 right-4 flex gap-2">
                      {reward.popular && (
                        <span className="px-2 py-1 bg-orange-50 text-orange-600 text-xs font-semibold rounded-lg border border-orange-200">
                          Popular
                        </span>
                      )}
                    </div>

                    <div className="text-center mb-4">
                      <div className="text-6xl mb-4">{reward.image}</div>
                      <h3 className="text-slate-900 font-semibold text-sm mb-2">{reward.name}</h3>
                      <p className="text-slate-500 text-xs">${reward.value.toFixed(2)} value</p>
                    </div>

                    <div className="flex items-center justify-between mb-4 p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Coins className="w-4 h-4 text-slate-600" />
                        <span className="text-slate-900 font-semibold text-sm">{reward.points.toLocaleString()}</span>
                      </div>
                      <span className="text-slate-500 text-xs">
                        {reward.value > 0 ? `${(reward.points / reward.value).toFixed(0)} pts/$` : '-'}
                      </span>
                    </div>

                    <button
                      onClick={() => handleRedeem(reward.id)}
                      disabled={userStats.totalPoints < reward.points || redeeming === reward.id}
                      className={`w-full py-2.5 rounded-lg transition-all font-medium text-sm ${
                        userStats.totalPoints >= reward.points
                          ? 'bg-slate-900 hover:bg-slate-800 text-white'
                          : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      }`}
                    >
                      {redeeming === reward.id ? (
                        <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                      ) : userStats.totalPoints >= reward.points ? (
                        'Redeem'
                      ) : (
                        'Insufficient Points'
                      )}
                    </button>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <div className="space-y-4">
            {recentActivity.length === 0 ? (
              <div className="text-center py-12 bg-slate-50 rounded-xl">
                <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No recent activity</p>
              </div>
            ) : (
              recentActivity.map((activity, index) => (
                <motion.div
                  key={activity.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-xl"
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${
                      activity.type === 'earned' ? 'bg-emerald-50' :
                      activity.type === 'redeemed' ? 'bg-slate-100' :
                      'bg-blue-50'
                    }`}>
                      {activity.type === 'earned' && <TrendingUp className="w-5 h-5 text-emerald-600" />}
                      {activity.type === 'redeemed' && <Gift className="w-5 h-5 text-slate-600" />}
                      {activity.type === 'bonus' && <Star className="w-5 h-5 text-blue-600" />}
                    </div>
                    <div>
                      <p className="text-slate-900 font-medium text-sm">{activity.description}</p>
                      <p className="text-slate-500 text-xs">{activity.time}</p>
                    </div>
                  </div>

                  <div className={`font-semibold text-sm ${
                    activity.points > 0 ? 'text-emerald-600' : 'text-slate-600'
                  }`}>
                    {activity.points > 0 ? '+' : ''}{activity.points.toLocaleString()} pts
                  </div>
                </motion.div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  )
}
